import { gql } from "~/__generated__"
import { useQuery } from "@apollo/client"
import { LoadingIndicatorCentered } from "~/components/LoadingIndicator"
import { EmptyData } from "~/components/EmptyData"
import { Error } from "~/ui/Error"
import {
  CandidateTestUnlockRequestsTable,
  CandidateTestUnlockRequestsTableProps,
} from "~/tests/CandidateTestUnlockRequestsTable"
import { useSearchParams } from "react-router-dom"
import { usePagination } from "~/common/usePagination"
import { useEffect, useState } from "react"
import {
  CandidateTestUnlockRequestSortEnum,
  SortDirectionEnum,
  CandidateTestUnlockRequestsOrganizationFilter,
  CandidateTestUnlockRequest_TableFragment,
} from "~/__generated__/graphql"
import { Pagination } from "~/ui/Pagination"

interface CandidateTestUnlockRequestsProps
  extends Pick<CandidateTestUnlockRequestsTableProps, "columns" | "viewPath"> {
  userId?: string
  testId?: string
  organizationId?: string
  defaultSort?: CandidateTestUnlockRequestSortEnum
  defaultDirection?: SortDirectionEnum
  searchParamsPrefix?: string
  perPage?: number
  filter?: CandidateTestUnlockRequestsOrganizationFilter
}

export class CandidateTestUnlockRequestSort {
  sort: CandidateTestUnlockRequestSortEnum
  direction: SortDirectionEnum

  constructor(
    sort: CandidateTestUnlockRequestSortEnum,
    direction: SortDirectionEnum
  ) {
    this.sort = sort
    this.direction = direction
  }

  reverseDirection() {
    return this.direction === SortDirectionEnum.Ascending
      ? SortDirectionEnum.Descending
      : SortDirectionEnum.Ascending
  }
}

export const CandidateTestUnlockRequests = ({
  organizationId,
  defaultSort = CandidateTestUnlockRequestSortEnum.CreatedAt,
  defaultDirection = SortDirectionEnum.Descending,
  searchParamsPrefix = "requests_",
  perPage = 20,
  filter,
  columns,
  viewPath,
}: CandidateTestUnlockRequestsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [candidateTestUnlockRequestsSort, setCandidateTestUnlockRequestsSort] =
    useState(
      new CandidateTestUnlockRequestSort(
        (searchParams.get(
          `${searchParamsPrefix}sort`
        ) as CandidateTestUnlockRequestSortEnum) || defaultSort,
        (searchParams.get(
          `${searchParamsPrefix}direction`
        ) as SortDirectionEnum) || defaultDirection
      )
    )

  useEffect(() => {
    if (candidateTestUnlockRequestsSort.sort === defaultSort) {
      searchParams.delete(`${searchParamsPrefix}sort`)
    } else {
      searchParams.set(
        `${searchParamsPrefix}sort`,
        candidateTestUnlockRequestsSort.sort
      )
    }

    if (candidateTestUnlockRequestsSort.direction === defaultDirection) {
      searchParams.delete(`${searchParamsPrefix}direction`)
    } else {
      searchParams.set(
        `${searchParamsPrefix}direction`,
        candidateTestUnlockRequestsSort.direction
      )
    }

    setSearchParams(searchParams)
  }, [
    candidateTestUnlockRequestsSort,
    setSearchParams,
    searchParams,
    searchParamsPrefix,
    defaultSort,
    defaultDirection,
  ])

  const { after, paginate, page } = usePagination({
    perPage: perPage,
    paramKey: `${searchParamsPrefix}page`,
  })
  const { loading, data, error } = useQuery(UNLOCK_REQUESTS_QUERY_DOCUMENT, {
    variables: {
      organizationId: organizationId!,
      first: perPage,
      after: after,
      sort: candidateTestUnlockRequestsSort.sort,
      direction: candidateTestUnlockRequestsSort.direction,
      filter: filter,
    },
  })

  const [unlockRequests, setUnlockRequests] = useState<
    CandidateTestUnlockRequest_TableFragment[]
  >([])

  useEffect(() => {
    if (data?.candidateTestUnlockRequests?.edges) {
      setUnlockRequests(
        data.candidateTestUnlockRequests.edges.map((edge) => edge.node)
      )
    }
  }, [data])

  if (loading) return <LoadingIndicatorCentered />
  if (error || !data) return <Error message="Error loading responses." />

  return (
    <>
      {unlockRequests.length > 0 ? (
        <>
          <CandidateTestUnlockRequestsTable
            data={unlockRequests}
            columns={columns}
            viewPath={viewPath}
            sort={candidateTestUnlockRequestsSort}
            setSort={setCandidateTestUnlockRequestsSort}
            onCandidateTestUnlockRequestAction={(id: string, newState: any) => {
              setUnlockRequests((prev) =>
                prev.map((request) =>
                  request.id === id ? { ...request, ...newState } : request
                )
              )
            }}
          />
          {data.candidateTestUnlockRequests.pageCount > 1 && (
            <Pagination
              page={page}
              pageCount={data.candidateTestUnlockRequests.pageCount}
              paginate={paginate}
            />
          )}
        </>
      ) : (
        <EmptyData>No requests made yet.</EmptyData>
      )}
    </>
  )
}

gql(`
  fragment CandidateTestUnlockRequest_Table on CandidateTestUnlockRequest {
    id
    note
    state
    createdAt

    candidateTest {
      id
      test {
        id
        name
      }
      user {
        id
        name
        email
      }
    }
  }
`)

const UNLOCK_REQUESTS_QUERY_DOCUMENT = gql(`
  query OrgUnlockRequests(
    $organizationId: ID!,
    $first: Int!,
    $after: String,
    $filter: CandidateTestUnlockRequestsOrganizationFilter,
    $sort: CandidateTestUnlockRequestSortEnum,
    $direction: SortDirectionEnum
  ) {
    candidateTestUnlockRequests(
      organizationId: $organizationId,
      filter: $filter,
      sort: $sort,
      direction: $direction,
      first: $first,
      after: $after
    ) {
      pageCount(first: $first)
      edges {
        node {
          ...CandidateTestUnlockRequest_Table
        }
      }
    }
  }
`)
